import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  ChevronLeft, 
  ChevronRight, 
  User, 
  Building2, 
  BookOpen, 
  Clock, 
  Calendar as CalendarIcon 
} from "lucide-react";
import { examAssignmentsAPI } from "@/services/api";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek } from "date-fns";

const MonthlyCalendarView = ({ scheduleFilter = "all" }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Get exam assignments for the current month
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['exam-assignments-calendar', format(currentDate, 'yyyy-MM')],
    queryFn: () => examAssignmentsAPI.getAll({ 
      limit: 10000,
      search: '',
      myLimit: 10000,
      otherLimit: 10000
    }),
  });

  // Get all days in the current month view (including leading/trailing days)
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const daysInCalendar = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Organize assignments by date with filtering
  const assignmentsByDate = useMemo(() => {
    const organized = {};
    
    if (data?.data) {
      let allAssignments = [];
      
      // Apply filtering
      switch (scheduleFilter) {
        case "my":
          allAssignments = data.data.myAssignments || [];
          break;
        case "company":
          allAssignments = data.data.otherAssignments || [];
          break;
        default: // "all"
          allAssignments = [
            ...(data.data.myAssignments || []),
            ...(data.data.otherAssignments || [])
          ];
          break;
      }
      
      allAssignments.forEach(assignment => {
        if (assignment.dateOfExam) {
          const dateKey = format(new Date(assignment.dateOfExam), 'yyyy-MM-dd');
          if (!organized[dateKey]) {
            organized[dateKey] = [];
          }
          organized[dateKey].push({
            ...assignment,
            isMyAssignment: data.data.myAssignments?.some(my => my._id === assignment._id)
          });
        }
      });
    }
    
    return organized;
  }, [data, scheduleFilter]);

  // ... keep existing code (navigateMonth, getAssignmentColor, getStatusColor, weekdays) the same

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const getAssignmentColor = (assignment) => {
    if (assignment.isMyAssignment) {
      switch (assignment.status) {
        case 'pending':
          return "bg-blue-500 text-white border-blue-600";
        case 'ongoing':
          return "bg-yellow-500 text-white border-yellow-600";
        case 'completed':
          return "bg-green-500 text-white border-green-600";
        case 'disqualified':
          return "bg-red-500 text-white border-red-600";
        default:
          return "bg-blue-500 text-white border-blue-600";
      }
    } else {
      switch (assignment.status) {
        case 'pending':
          return "bg-purple-400 text-white border-purple-500";
        case 'ongoing':
          return "bg-orange-400 text-white border-orange-500";
        case 'completed':
          return "bg-teal-400 text-white border-teal-500";
        case 'disqualified':
          return "bg-pink-400 text-white border-pink-500";
        default:
          return "bg-purple-400 text-white border-purple-500";
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "ongoing":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "completed":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "disqualified":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="h-full">
        <Card className="h-full flex flex-col">
          <CardHeader className="pb-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-b flex-shrink-0">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-blue-600" />
                {format(currentDate, 'MMMM yyyy')}
              </CardTitle>
              <div className="flex gap-1">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigateMonth('prev')}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-3 w-3" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setCurrentDate(new Date())}
                  className="h-8 px-2 text-xs"
                >
                  Today
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => refetch()}
                  className="h-8 px-2 text-xs"
                  disabled={isLoading}
                >
                  {isLoading ? "..." : "Refresh"}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigateMonth('next')}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-2 flex-1 overflow-hidden">
            <div className="h-full flex flex-col">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekdays.map(day => (
                  <div key={day} className="p-1 text-center text-xs font-semibold text-gray-600 bg-gray-50 rounded">
                    {day}
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-7 gap-1 flex-1 min-h-0">
                {daysInCalendar.map(day => {
                  const dateKey = format(day, 'yyyy-MM-dd');
                  const dayAssignments = assignmentsByDate[dateKey] || [];
                  const isToday = isSameDay(day, new Date());
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  
                  return (
                    <div 
                      key={day.toISOString()} 
                      className={`min-h-0 p-1 border rounded transition-all duration-200 hover:shadow-sm ${
                        isToday 
                          ? 'bg-blue-50 border-blue-200' 
                          : isCurrentMonth
                          ? 'bg-white border-gray-200 hover:bg-gray-50'
                          : 'bg-gray-50 border-gray-100'
                      }`}
                    >
                      <div className={`text-xs font-semibold mb-1 ${
                        isToday 
                          ? 'text-blue-600' 
                          : isCurrentMonth 
                          ? 'text-gray-700' 
                          : 'text-gray-400'
                      }`}>
                        {format(day, 'd')}
                      </div>
                      
                      <ScrollArea className="h-16">
                        <div className="space-y-0.5">
                          {dayAssignments.slice(0, 3).map(assignment => (
                            <Tooltip key={assignment._id}>
                              <TooltipTrigger asChild>
                                <div
                                  className={`${getAssignmentColor(assignment)} 
                                    p-0.5 rounded text-xs cursor-pointer border-l-2 transform hover:scale-105 transition-transform`}
                                >
                                  <div className="text-xs font-medium truncate flex items-center gap-1">
                                    {format(new Date(assignment.dateOfExam), 'HH:mm')}
                                    {assignment.aiModuleId && (
                                      <span className="text-xs opacity-75">🤖</span>
                                    )}
                                  </div>
                                  <div className="text-xs truncate opacity-90">
                                    {assignment.candidateId?.name}
                                  </div>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <User className="h-4 w-4 text-blue-500" />
                                    <span className="font-semibold">
                                      {assignment.candidateId?.name}
                                    </span>
                                    {assignment.isMyAssignment && (
                                      <Badge variant="secondary" className="text-xs">
                                        Your Assignment
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <BookOpen className="h-4 w-4 text-green-500" />
                                    <span>{assignment.examId?.name}</span>
                                    {assignment.aiModuleId ? (
                                      <Badge className="text-xs bg-purple-100 text-purple-800">
                                        AI Assessment
                                      </Badge>
                                    ) : (
                                      <Badge className="text-xs bg-blue-100 text-blue-800">
                                        Manual
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4 text-purple-500" />
                                    <span>{assignment.companyId?.name || 'Company'}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-orange-500" />
                                    <span>
                                      {format(new Date(assignment.dateOfExam), "MMM d, yyyy 'at' HH:mm")}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge className={getStatusColor(assignment.status)}>
                                      {assignment.status.toUpperCase()}
                                    </Badge>
                                  </div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                          
                          {dayAssignments.length > 3 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="text-xs text-gray-500 bg-gray-100 p-0.5 rounded cursor-pointer hover:bg-gray-200 transition-colors">
                                  +{dayAssignments.length - 3} more
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="space-y-1">
                                  {dayAssignments.slice(3).map(assignment => (
                                    <div key={assignment._id} className="text-sm">
                                      {assignment.candidateId?.name} - {assignment.examId?.name}
                                    </div>
                                  ))}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default MonthlyCalendarView;